import {
  IconCarrot,
  IconChevronRight,
  IconMail,
  IconMapPin,
  IconMenu,
  IconPhone,
  IconShare,
  IconX,
  IconBell,
  IconBed,
  IconArmchair2,
  IconReceipt,
  IconRefresh,
} from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { iconStroke } from "../config/config";
import { getCart, setCart, getQRMenuInit, callWaiter, getActiveTableOrRoomBill } from "../controllers/qrmenu.controller";

const SafeIconPrinter = ({ size = 20, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);
import { CURRENCIES } from "../config/currencies.config";
import { getImageURL } from "../helpers/ImageHelper";
import toast from "react-hot-toast";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { getQRMenuLink } from "../helpers/QRMenuHelper";
import { useTranslation } from "react-i18next";
import LanguageChanger from "../components/LanguageChanger";
import { useTheme } from "../contexts/ThemeContext";

export default function QRMenuPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const qrcode = params.qrcode;
  const {theme} = useTheme();
  
  const [isBellClicked, setIsBellClicked] = useState(false);
  const [promptLocation, setPromptLocation] = useState("");

  const [searchParams] = useSearchParams();

  const getParam = (keys) => {
    try {
      for (const k of keys) {
        const v = searchParams.get(k) || (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get(k) : null);
        if (v && String(v).trim()) return String(v).trim();
      }
    } catch (e) {}
    return null;
  };

  const initialTable = getParam(["table", "tableId", "table_id"]) || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("qr_table") : null);
  const initialRoom = getParam(["room", "roomName", "room_name", "roomId", "room_id", "room_no", "roomNumber"]) || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("qr_room") : null);

  const [encryptedTableId, setEncryptedTableId] = useState(initialTable);
  const [room, setRoom] = useState(initialRoom);

  const [isBillLoading, setIsBillLoading] = useState(false);
  const [isBillRefreshing, setIsBillRefreshing] = useState(false);
  const [billData, setBillData] = useState(null);

  useEffect(() => {
    const t = getParam(["table", "tableId", "table_id"]);
    const r = getParam(["room", "roomName", "room_name", "roomId", "room_id", "room_no", "roomNumber"]);
    if (t) {
      try { sessionStorage.setItem("qr_table", t); } catch (e) {}
      setEncryptedTableId(t);
    }
    if (r) {
      try { sessionStorage.setItem("qr_room", r); } catch (e) {}
      setRoom(r);
    }
  }, [searchParams]);

  const [state, setState] = useState({
    isLoading: true,
    storeSettings: null,
    storeTable: null,
    serviceCharge: null,
    categories: [],
    currentCategory: "all",
    menuItems: [],
    searchQuery: "",
    currentItem: null,
    cartItems: [],
    currentItemId: null,
    currency: "",
  });

  useEffect(() => {
    _getQRMenu(qrcode);
  }, [qrcode, encryptedTableId]);

  const _getQRMenu = async (qrcode) => {
    try {
      const res = await getQRMenuInit(qrcode, encryptedTableId);

      const storedCart = getCart();
      if (res.status == 200) {
        const data = res.data;

        const currency = CURRENCIES.find(
          (c) => c.cc == data?.storeSettings?.currency
        );

        setState({
          ...state,
          isLoading: false,
          storeSettings: data?.storeSettings,
          categories: data?.categories,
          menuItems: data?.menuItems,
          storeTable: data?.storeTable || null,
          serviceCharge: data?.serviceCharge || null,
          cartItems: [...storedCart],
          currency: currency?.symbol || "",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const {
    isLoading,
    storeSettings,
    categories,
    menuItems,
    currentCategory,
    searchQuery,
    currentItem,
    cartItems,
    currentItemId,
    currency,
    serviceCharge,
  } = state;

  const storeName = storeSettings?.store_name || "";
  const address = storeSettings?.address || "";
  const phone = storeSettings?.phone || "";
  const email = storeSettings?.email || "";
  const storeImage = storeSettings?.store_image || "";
  const is_qr_menu_enabled = storeSettings?.is_qr_menu_enabled || false;
  const is_qr_order_enabled = storeSettings?.is_qr_order_enabled || false;
  const is_qr_view_bill_enabled = Boolean(storeSettings?.is_qr_view_bill_enabled);

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="container mx-auto px-4 flex h-screen items-center justify-center">
          {t("qr_menu.loading_message")}
        </div>
      </div>
    );
  }

  if (!qrcode) {
    return (
      <div className="w-full">
        <div className="container mx-auto px-4 flex h-screen items-center justify-center">
          {t("qr_menu.broken_link")}
        </div>
      </div>
    );
  }

  if (!is_qr_menu_enabled) {
    return (
      <div className="w-full">
        <div className="container mx-auto px-4 flex h-screen items-center justify-center">
          {t("qr_menu.menu_not_available")}
        </div>
      </div>
    );
  }

  const QR_MENU_LINK = getQRMenuLink(qrcode);

  const btnShare = async () => {
    const shareData = {
      title: t("qr_menu.menu"),
      text: t("qr_menu.menu"),
      url: QR_MENU_LINK,
    };

    try {
      if (navigator.canShare) {
        if (navigator?.canShare(shareData)) {
          await navigator.share(shareData);
        }
      } else {
        await navigator.clipboard.writeText(QR_MENU_LINK);
        toast.success(t("qr_menu.menu_link_copied"));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const btnOpenMenuItemDetail = (
    addons,
    variants,
    category_id,
    category_title,
    id,
    image,
    price,
    tax_id,
    tax_rate,
    tax_title,
    tax_type,
    title
  ) => {
    setState({
      ...state,
      currentItem: {
        addons: addons,
        variants: variants,
        category_id: category_id,
        category_title: category_title,
        id,
        image,
        price,
        tax_id,
        tax_rate,
        tax_title,
        tax_type,
        title,
      },
    });
    document.getElementById("modal_item_detail").showModal();
  };

  // cart
  function addItemToCart(item) {
    const qtyToAdd = (item.quantity && parseInt(item.quantity, 10) > 0) ? parseInt(item.quantity, 10) : 1;

    const getAddonKey = (ci) => {
      if (Array.isArray(ci.addons_ids) && ci.addons_ids.length > 0) {
        return ci.addons_ids
          .map(a => (typeof a === 'object' && a !== null ? String(a.id) : String(a)).trim())
          .filter(Boolean)
          .sort()
          .join(',');
      }
      return '';
    };

    const getNormalizedVariant = (val) => (val !== undefined && val !== null && val !== "" && val !== 0 && val !== "0") ? String(val) : "";

    const itemVariantId = getNormalizedVariant(item.variant_id);
    const itemAddonKey = getAddonKey(item);
    const itemNotes = (item.notes || '').trim();

    const currentCart = cartItems || [];
    const existingIndex = currentCart.findIndex(ci => {
      const ciVariantId = getNormalizedVariant(ci.variant_id);
      const ciAddonKey = getAddonKey(ci);
      const ciNotes = (ci.notes || '').trim();

      return (
        String(ci.id) === String(item.id) &&
        ciVariantId === itemVariantId &&
        ciAddonKey === itemAddonKey &&
        ciNotes === itemNotes &&
        Math.abs(Number(String(ci.price || 0).replace(/,/g, "")) - Number(String(item.price || 0).replace(/,/g, ""))) < 0.001
      );
    });

    let newCart = [...currentCart];
    if (existingIndex !== -1) {
      const existingItem = newCart[existingIndex];
      const currentQty = parseInt(existingItem.quantity, 10) || 1;
      newCart[existingIndex] = {
        ...existingItem,
        quantity: currentQty + qtyToAdd
      };
    } else {
      const modifiedItem = {
        ...item,
        quantity: qtyToAdd,
        notes: item.notes || null,
      };
      newCart.push(modifiedItem);
    }

    setState({
      ...state,
      cartItems: newCart,
    });
    setCart(newCart);
  }

  const btnAddMenuItemToCartWithVariantsAndAddon = () => {
    let price = 0;
    let selectedVariantId = null;
    const selectedAddonsId = [];

    const itemVariants = document.getElementsByName("variants");
    itemVariants.forEach((item) => {
      if (item.checked) {
        selectedVariantId = item.value;
        return;
      }
    });

    price = parseFloat(String(currentItem.price).replace(/,/g, ""));

    const itemAddons = document.getElementsByName("addons");
    itemAddons.forEach((item) => {
      if (item.checked) {
        selectedAddonsId.push(item.value);
      }
    });

    const addons = currentItem?.addons || [];
    const variants = currentItem?.variants || [];

    let selectedVariant = null;
    if (selectedVariantId) {
      selectedVariant = variants.find((v) => v.id == selectedVariantId);
      price = parseFloat(String(selectedVariant.price).replace(/,/g, ""));
    }

    let selectedAddons = [];
    if (selectedAddonsId.length > 0) {
      selectedAddons = selectedAddonsId.map((addonId) =>
        addons.find((addon) => addon.id == addonId)
      );
      selectedAddons.forEach((addon) => {
        const addonPrice = parseFloat(String(addon.price).replace(/,/g, ""));
        price += addonPrice;
      });
    }

    const itemCart = {
      ...currentItem,
      price: price,
      variant_id: selectedVariantId,
      variant: selectedVariant,
      addons_ids: selectedAddonsId,
      addons: selectedAddons,
    };

    addItemToCart(itemCart);
  };

  const handleCallWaiter = async () => {
    if (isBellClicked) return;
    
    const activeRoom = room || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("qr_room") : null);
    const activeTable = encryptedTableId || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("qr_table") : null);

    if (!activeTable && !activeRoom) {
      const modal = document.getElementById("modal_call_service_prompt");
      if (modal) {
        modal.showModal();
        return;
      }
      toast.error(t("qr_menu.table_not_found") || "Please provide your room or table number");
      return;
    }
    
    try {
      setIsBellClicked(true);
      const isRoom = !!activeRoom;
      const cleanRoom = activeRoom ? (String(activeRoom).toLowerCase().startsWith("room") ? activeRoom : `Room ${activeRoom}`) : null;
      toast.loading(isRoom ? `Calling service for ${cleanRoom}...` : "Calling waiter...", { id: "callWaiter" });
      await callWaiter(qrcode, activeTable, activeRoom);
      toast.success(isRoom ? `Service called for ${cleanRoom}!` : "Waiter called!", { id: "callWaiter" });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to call service", { id: "callWaiter" });
      setIsBellClicked(false);
    }
  };

  const handleConfirmPromptCall = async (e) => {
    e.preventDefault();
    if (!promptLocation || !promptLocation.trim()) {
      toast.error("Please enter your Room or Table number");
      return;
    }
    const entered = promptLocation.trim();
    try {
      sessionStorage.setItem("qr_room", entered);
    } catch (e) {}
    setRoom(entered);
    const modal = document.getElementById("modal_call_service_prompt");
    if (modal) modal.close();

    try {
      setIsBellClicked(true);
      const cleanLabel = entered.toLowerCase().startsWith("room") || entered.toLowerCase().startsWith("table") ? entered : `Room ${entered}`;
      toast.loading(`Calling service for ${cleanLabel}...`, { id: "callWaiter" });
      await callWaiter(qrcode, null, entered);
      toast.success(`Service called for ${cleanLabel}!`, { id: "callWaiter" });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to call service", { id: "callWaiter" });
      setIsBellClicked(false);
    }
  };

  const fetchActiveBill = async (isRefresh = false) => {
    const activeTable = encryptedTableId || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("qr_table") : null);
    const activeRoom = room || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("qr_room") : null);

    if (!activeTable && !activeRoom && !state.storeTable?.table_title) {
      const modal = document.getElementById("modal_call_service_prompt");
      if (modal) {
        modal.showModal();
        return false;
      }
      toast.error("Please provide your room or table number to view bill");
      return false;
    }

    try {
      if (isRefresh) {
        setIsBillRefreshing(true);
        toast.loading("Refreshing active bill...", { id: "fetchBill" });
      } else {
        setIsBillLoading(true);
      }

      const res = await getActiveTableOrRoomBill(qrcode, {
        tableId: activeTable,
        roomName: activeRoom
      });

      if (res?.data?.success) {
        setBillData(res.data);
        if (isRefresh) {
          toast.success("Bill updated with latest orders!", { id: "fetchBill" });
        }
        return true;
      } else {
        toast.error(res?.data?.message || "Could not load active bill", { id: "fetchBill" });
        return false;
      }
    } catch (err) {
      console.error("fetchActiveBill error:", err);
      toast.error(err?.response?.data?.message || "Failed to load active bill", { id: "fetchBill" });
      return false;
    } finally {
      setIsBillLoading(false);
      setIsBillRefreshing(false);
    }
  };

  const handleOpenViewBill = async () => {
    const modal = document.getElementById("modal_view_bill");
    if (modal) modal.showModal();
    await fetchActiveBill(false);
  };

  const handlePrintEstimateBill = () => {
    window.print();
  };

  return (
    <div className="w-full">
      <div className="container mx-auto">
        {/* appbar */}
        <div className="px-4 py-4 w-full max-w-5xl mx-auto flex gap-2 sticky z-50 top-0 shadow-sm rounded-b-3xl bg-white/95 dark:bg-black/95 backdrop-blur items-center">
          <input
            type="search"
            name="search"
            id="search"
            className="bg-gray-100 dark:bg-restro-bg-gray rounded-2xl outline-none px-4 py-2 flex-1 text-sm md:text-base"
            placeholder={t("qr_menu.search_placeholder")}
            value={searchQuery}
            onChange={(e) => {
              setState({
                ...state,
                searchQuery: e.target.value,
              });
            }}
          />

          {/* Location Indicator Badge */}
          {room && (
            <div className="hidden sm:flex items-center gap-1 px-3 py-1.5 bg-restro-green/10 text-restro-green border border-restro-green rounded-2xl text-xs font-bold whitespace-nowrap">
              <IconBed size={16} />
              <span>{String(room).toLowerCase().startsWith("room") ? room : `Room ${room}`}</span>
            </div>
          )}
          {!room && state.storeTable?.table_title && (
            <div className="hidden sm:flex items-center gap-1 px-3 py-1.5 bg-restro-green/10 text-restro-green border border-restro-green rounded-2xl text-xs font-bold whitespace-nowrap">
              <IconArmchair2 size={16} />
              <span>Table {state.storeTable.table_title}</span>
            </div>
          )}

          {is_qr_view_bill_enabled && (
            <button
              className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30 rounded-2xl px-3 py-2 transition flex items-center gap-1 font-semibold text-xs md:text-sm shadow-sm whitespace-nowrap active:scale-95"
              onClick={handleOpenViewBill}
              title="View Live Bill / Estimate"
            >
              <IconReceipt stroke={iconStroke} size={18} />
              <span className="hidden md:inline">View Bill</span>
            </button>
          )}

          <button
            className="bg-gray-100 dark:bg-restro-gray hover:bg-gray-200 dark:hover:bg-restro-bg-gray rounded-2xl px-3 py-2 text-restro-green disabled:opacity-50 transition flex items-center gap-1 font-semibold text-xs md:text-sm shadow-sm"
            onClick={handleCallWaiter}
            disabled={isBellClicked}
            title={room ? `Call Room Service (${String(room).toLowerCase().startsWith("room") ? room : `Room ${room}`})` : "Call Waiter"}
          >
            <IconBell stroke={iconStroke} size={20} className={isBellClicked ? "" : "animate-bounce"} />
            <span className="hidden md:inline">{room ? "Room Service" : "Call Waiter"}</span>
          </button>
          <button
            className="bg-gray-100 dark:bg-restro-gray rounded-2xl px-2 py-2"
            onClick={() =>
              document.getElementById("modal_store_details").showModal()
            }
          >
            <IconMenu stroke={iconStroke} size={20}/>
          </button>
        </div>
        {/* appbar */}

        {/* Banner for View Bill if active table or room */}
        {is_qr_view_bill_enabled && (room || state.storeTable?.table_title || encryptedTableId) && (
          <div className="px-4 py-2.5 max-w-5xl mx-auto mt-2 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-center justify-between gap-3 text-xs md:text-sm">
            <div className="flex items-center gap-2 text-amber-900 dark:text-amber-300 font-medium">
              <IconReceipt size={18} className="text-amber-600 shrink-0" />
              <span>
                Active order on <strong>{room ? (String(room).toLowerCase().startsWith("room") ? room : `Room ${room}`) : `Table ${state.storeTable?.table_title || ""}`}</strong>? View your live estimate bill anytime.
              </span>
            </div>
            <button
              onClick={handleOpenViewBill}
              className="btn btn-xs md:btn-sm bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl border-none shadow whitespace-nowrap"
            >
              View Bill
            </button>
          </div>
        )}

        {/* store details: name, phone, address, email, share menu link */}
        <div className="py-4 px-6 md:px-6 w-full max-w-5xl mx-auto rounded-3xl mt-4 flex flex-col md:flex-row md:border md:gap-6 md:items-center md:justify-center border-restro-border-green">
          <div className="flex items-center gap-1 md:gap-4 flex-col">
            <div className="">
              {storeImage && (
                <img
                  src={getImageURL(storeImage)}
                  className="w-24 h-24 md:w-32 md:h-32 rounded-xl shadow-md"
                  alt={`${storeName} Logo`}
                />
              )}
            </div>
            <h3 className="font-bold text-lg md:text-2xl mt-2">{storeName}</h3>
          </div>

          <div>
            {address && (
              <div className="flex gap-2 items-start text-sm md:text-lg mt-2">
                <div>
                  <IconMapPin stroke={iconStroke} size={20} />
                </div>
                <p className="text-wrap break-words">{address}</p>
              </div>
            )}
            <div className="flex flex-col md:flex-row gap-2 md:gap-4 mt-2">
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="flex gap-2 items-center text-sm md:text-lg"
                >
                  <IconPhone stroke={iconStroke} size={20} />
                  {phone}
                </a>
              )}
            </div>
            {email && (
              <a
                href={`mailto:${email}`}
                className="flex gap-2 items-center text-sm md:text-lg text-wrap max-w-full break-words mt-2"
              >
                <div>
                  <IconMail stroke={iconStroke} size={20} />
                </div>
                <p>{email}</p>
              </a>
            )}
          </div>
        </div>
        {/* store details: name, phone, address, email, share menu link */}

        {/* categories */}
        <div className="p-2 w-full max-w-5xl overflow-x-auto mx-auto mt-4 flex gap-2 sticky top-20 z-40 bg-white/80 dark:bg-black backdrop-blur">
          <label>
            <input
              onChange={(e) => {
                setState({
                  ...state,
                  currentCategory: e.target.value,
                });
              }}
              type="radio"
              name="category"
              id={"all"}
              value={"all"}
              className="peer hidden"
              defaultChecked
            />
            <label
              htmlFor={"all"}
              className="text-xs cursor-pointer rounded-full px-4 py-3 bg-white dark:bg-restro-card-bg border-restro-border-green border hover:bg-gray-50 active:scale-95 transition text-gray-500 dark:text-white checked:bg-restro-green peer-checked:text-white peer-checked:bg-restro-green flex"
            >
              <p>{t("qr_menu.all")}</p>
            </label>
          </label>
          {categories.filter(category => category.is_enabled).map((category, index) => {
            const { id, title } = category;
            return (
              <div key={index} className="min-w-fit">
                <input
                  onChange={(e) => {
                    setState({
                      ...state,
                      currentCategory: e.target.value,
                    });
                  }}
                  type="radio"
                  name="category"
                  id={id}
                  value={id}
                  className="peer hidden"
                />
                <label
                  htmlFor={id}
                  className="text-xs cursor-pointer rounded-full px-4 py-3 bg-white dark:bg-restro-card-bg border-restro-border-green border hover:bg-gray-50 active:scale-95 transition text-gray-500 dark:text-white checked:bg-restro-green peer-checked:text-white peer-checked:bg-restro-green flex"
                >
                  <p>{title}</p>
                </label>
              </div>
            );
          })}
        </div>
        {/* categories */}

        {/* menu items */}
        <div className="p-2 w-full max-w-5xl mx-auto mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 justify-center items-center">
          {menuItems
          .filter((menuItem) => menuItem.is_enabled)
            .filter((item) => {
              const { category_id } = item;
              if (currentCategory == "all") {
                return !category_id || categories.find(category => category.id === category_id && category.is_enabled);
              }
              if (currentCategory == category_id) {
                return true;
              }
              return false;
            })
            .filter((menuItem) => {
              if (!searchQuery) {
                return true;
              }
              return new String(menuItem.title)
                .trim()
                .toLowerCase()
                .includes(searchQuery.trim().toLowerCase());
            })
            .map((item, i) => {
              const {
                addons,
                variants,
                category_id,
                category_title,
                id,
                image,
                price,
                tax_id,
                tax_rate,
                tax_title,
                tax_type,
                title,
                description,
              } = item;
              // addon {id, item_id, title, price}
              // variant {id, item_id, title, price}

              const imageURL = getImageURL(image);
              const hasVariantOrAddon =
                variants?.length > 0 || addons?.length > 0;

              return (
                <div
                  key={id}
                  className={`w-full rounded-3xl px-4 py-4 flex gap-4 ${
                    !is_qr_order_enabled
                      ? "hover:bg-gray-100 dark:hover:bg-restro-button-hover cursor-pointer"
                      : ""
                  }`}
                  onClick={
                    !is_qr_order_enabled
                      ? () =>
                          btnOpenMenuItemDetail(
                            addons,
                            variants,
                            category_id,
                            category_title,
                            id,
                            image,
                            price,
                            tax_id,
                            tax_rate,
                            tax_title,
                            tax_type,
                            title
                          )
                      : undefined
                  }
                >
                  <div>
                    <div className="rounded-2xl w-32 h-32 object-cover relative bg-gray-200 dark:bg-restro-card-bg flex items-center justify-center text-gray-500 ">
                      {image ? (
                        <img
                          src={imageURL}
                          alt={title}
                          loading="lazy"
                          className="w-full h-full absolute top-0 left-0 rounded-2xl object-cover z-0"
                        />
                      ) : (
                        <IconCarrot />
                      )}
                      {/* Add button overlapping image */}
                      {is_qr_order_enabled && (
                        <button
                          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 text-white bg-restro-green py-1 px-6 rounded-lg font-bold hover:bg-restro-green-button-hover"
                          onClick={(e) => {
                            e.stopPropagation();

                            if (hasVariantOrAddon) {
                              btnOpenMenuItemDetail(
                                addons,
                                variants,
                                category_id,
                                category_title,
                                id,
                                image,
                                price,
                                tax_id,
                                tax_rate,
                                tax_title,
                                tax_type,
                                title
                              );
                            } else {
                              addItemToCart(item);
                            }
                          }}
                        >
                          {t("qr_menu.add")}
                        </button>
                      )}
                    </div>

                    {is_qr_order_enabled &&
                      (variants.length > 0 || addons.length > 0) && (
                        <div className="text-center mt-5">
                          <p className="text-xs text-gray-500">{t("qr_menu.customisable")}</p>
                        </div>
                      )}
                  </div>

                  <div className="flex-1">
                    <p className="text-lg md:text-xl line-clamp-2 text-ellipsis">{title}</p>
                    <p className="line-clamp-2 text-ellipsis text-xs md:text-sm text-gray-500">{description}</p>
                    <p className="text-sm text-restro-green font-bold md:text-lg">
                      {currency}
                      {price}
                    </p>
                    {category_id ? (
                      <p className="mt-2 text-xs text-gray-500 md:text-md">
                        {t("qr_menu.category")}: {category_title}
                      </p>
                    ):<></>}
                    <div className="flex gap-2 mt-2 text-xs text-gray-500 md:text-md">
                      {variants.length > 0 && (
                        <p>{variants.length} {t("qr_menu.variants_available")}</p>
                      )}
                      {addons.length > 0 && (
                        <p>{addons.length} {t("qr_menu.addons_available")}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        {/* menu items */}

        {is_qr_order_enabled && <div className="h-28" />}

        {is_qr_order_enabled && cartItems.length > 0 && (
          <div
            className="p-2 w-full max-w-5xl mx-auto fixed bottom-0 left-0 right-0 bg-white dark:bg-black shadow-lg flex justify-between items-center rounded-2xl"
            style={{ boxShadow: "0 -4px 10px rgba(0, 0, 0, 0.1)" }}
          >
            <button
              onClick={() => {
                navigate(`/m/${qrcode}/cart`, {
                  state: { storeTable: state.storeTable, currency: currency, serviceCharge: serviceCharge, room: room },
                });
              }}
              className="bg-restro-green text-white py-4 px-6 flex justify-between items-center rounded-xl w-full"
            >
              <p className="text-md font-bold md:text-xl">
                {cartItems.length} {t("qr_menu.items_added")}
              </p>
              <p className="text-white text-lg font-bold px-2 rounded-lg flex gap-1 items-center md:text-2xl">
                {t("qr_menu.view_cart")}
                <IconChevronRight size={22} stroke={3} />
              </p>
            </button>
          </div>
        )}

        {/* store details: name, phone, address, email, share menu link */}
        <dialog
          id="modal_store_details"
          className="modal modal-bottom sm:modal-middle"
        >
          <div className='modal-box border border-restro-border-green dark:rounded-2xl'>
            <div className="flex justify-between gap-4">
              <LanguageChanger className="rounded-lg px-2 py-2 transition active:scale-95 w-auto border border-restro-gray focus:border-restro-gray" />

              <div className="flex gap-4">
                <button className="btn btn-circle  bg-restro-bg-gray hover:bg-restro-button-hover" onClick={btnShare}>
                  <IconShare stroke={iconStroke} />
                </button>
                <form method="dialog">
                  <button className="btn btn-circle bg-restro-bg-gray hover:bg-restro-button-hover">
                    <IconX stroke={iconStroke} />
                  </button>
                </form>
              </div>
            </div>
            <div className="flex flex-col items-center mb-4 mt-10 gap-1 md:gap-4">
              <div className="">
                {storeImage && (
                  <img
                    src={getImageURL(storeImage)}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-xl shadow-md"
                    alt={`${storeName} Logo`}
                  />
                )}
              </div>
              <h3 className="font-bold text-lg md:text-2xl mt-2">
                {storeName}
              </h3>
            </div>
            {address && (
              <div className="flex gap-2 items-center text-sm md:text-lg mt-2">
                <div>
                  <IconMapPin stroke={iconStroke} size={20} />
                </div>
                <p>{address}</p>
              </div>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="flex gap-2 items-center text-sm md:text-lg mt-2"
              >
                <div>
                  <IconMail stroke={iconStroke} size={20} />
                </div>
                <p>{email}</p>
              </a>
            )}
            <div className="flex flex-col gap-2 mt-2">
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="flex gap-2 items-center text-sm md:text-lg"
                >
                  <div>
                    <IconPhone stroke={iconStroke} size={20} />
                  </div>
                  <p>{phone}</p>
                </a>
              )}
            </div>
          </div>
        </dialog>
        {/* store details: name, phone, address, email, share menu link */}

        {/* dialog for detail menu item view with addon, variants */}
        <dialog
          id="modal_item_detail"
          className="modal modal-bottom sm:modal-middle"
        >
          <div className='bg-white dark:bg-black border border-restro-border-green px-4 py-4 m-2 rounded-2xl'>
            <div className="absolute top-4 right-4">
              <form method="dialog">
                <button className="btn btn-circle">
                  <IconX stroke={iconStroke} />
                </button>
              </form>
            </div>

            <div className="w-full flex gap-4">
              <div className="rounded-2xl w-32 h-32 object-cover relative bg-gray-200 flex items-center justify-center text-gray-500 ">
                {currentItem?.image ? (
                  <img
                    src={getImageURL(currentItem?.image)}
                    alt={currentItem?.title}
                    className="w-full h-full absolute top-0 left-0 rounded-2xl object-cover z-0"
                  />
                ) : (
                  <IconCarrot />
                )}
              </div>
              <div className="flex-1">
                <p className="text-lg">{currentItem?.title}</p>
                <p className="text-sm text-restro-green font-bold">
                  {currency}
                  {currentItem?.price}
                </p>
                {currentItem?.category_id && (
                  <p className="mt-2 text-xs text-gray-500">
                    {t("qr_menu.category")}: {currentItem?.category_title}
                  </p>
                )}
                <div className="flex gap-2 mt-2 text-xs text-gray-500">
                  {currentItem?.variants.length > 0 && (
                    <p>{currentItem?.variants.length} {t("qr_menu.variants_available")}</p>
                  )}
                  {currentItem?.addons.length > 0 && (
                    <p>{currentItem?.addons.length} {t("qr_menu.addons_available")}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="my-4 flex flex-col sm:flex-row gap-2">
              {currentItem?.variants.length > 0 && (
                <div className="flex-1">
                  <h3>{t("qr_menu.variants")}</h3>
                  <div className="flex flex-col gap-2 mt-2">
                    {currentItem?.variants?.map((variant, index) => {
                      const { id, title, price } = variant;
                      return (
                        <label
                          key={index}
                          className="cursor-pointer label justify-start gap-2"
                        >
                          <input
                            type="radio"
                            className="radio"
                            name="variants"
                            id={id}
                            value={id}
                            defaultChecked={index === 0}
                          />
                          <span className="label-text">
                            {title} - {currency}
                            {price}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {currentItem?.addons.length > 0 && (
                <div className="flex-1">
                  <h3>{t("qr_menu.addons")}</h3>
                  <div className="flex flex-col gap-2 mt-2">
                    {currentItem?.addons?.map((addon, index) => {
                      const { id, title, price } = addon;
                      return (
                        <label
                          key={index}
                          className="cursor-pointer label justify-start gap-2"
                        >
                          <input
                            type="checkbox"
                            name="addons"
                            className="checkbox checkbox-sm"
                            value={id}
                          />
                          <span className="label-text">
                            {title} (+{currency}
                            {price})
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {is_qr_order_enabled && (
              <div className="modal-action flex justify-end w-full">
                <form method="dialog" className="w-full">
                  <button
                    onClick={() => {
                      btnAddMenuItemToCartWithVariantsAndAddon();
                    }}
                    className="w-full rounded-lg hover:bg-green-800 transition active:scale-95 hover:shadow-lg px-6 py-2 bg-restro-green text-white font-bold"
                  >
                    {t("qr_menu.add_to_cart")}
                  </button>
                </form>
              </div>
            )}

            <p className="text-gray-500 text-xs mt-1">
              *{t("qr_menu.price_disclaimer")}
            </p>
          </div>
        </dialog>
        {/* dialog for detail menu item view */}

        {/* Call Waiter / Service Prompt modal if opened without table/room in URL */}
        <dialog id="modal_call_service_prompt" className="modal modal-bottom sm:modal-middle">
          <div className="modal-box border border-restro-border-green dark:rounded-2xl">
            <h3 className="font-bold text-lg flex items-center gap-2 text-restro-green">
              <IconBell size={24} /> Call Waiter / Room Service
            </h3>
            <p className="py-2 text-sm text-gray-500">
              Please enter your Room or Table number so our staff knows where to assist you:
            </p>
            <form onSubmit={handleConfirmPromptCall} className="space-y-4 mt-2">
              <input
                type="text"
                autoFocus
                placeholder="e.g. Room 101 or Table 5"
                value={promptLocation}
                onChange={(e) => setPromptLocation(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-restro-gray bg-gray-50 dark:bg-restro-bg-gray outline-none focus:border-restro-green text-base font-semibold"
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => document.getElementById("modal_call_service_prompt").close()}
                  className="px-4 py-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-restro-gray"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-restro-green text-white font-bold hover:bg-restro-green-button-hover shadow-md"
                >
                  Confirm
                </button>
              </div>
            </form>
          </div>
        </dialog>

        {/* Live Estimate Bill Modal */}
        <dialog id="modal_view_bill" className="modal modal-bottom sm:modal-middle">
          <div className="modal-box p-0 max-w-lg bg-white dark:bg-restro-bg-gray border border-gray-200 dark:border-restro-gray rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-5 py-4 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border-b border-gray-100 dark:border-restro-gray flex items-center justify-between no-print">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-bold">
                <IconReceipt size={22} />
                <span className="text-base md:text-lg">Live Order Bill</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fetchActiveBill(true)}
                  disabled={isBillRefreshing || isBillLoading}
                  className="btn btn-sm btn-ghost btn-circle text-gray-500 hover:text-amber-600 disabled:opacity-50"
                  title="Refresh bill"
                >
                  <IconRefresh size={18} className={isBillRefreshing ? "animate-spin text-amber-600" : ""} />
                </button>
                <form method="dialog">
                  <button className="btn btn-sm btn-ghost btn-circle text-gray-400 hover:text-gray-600">
                    <IconX size={20} />
                  </button>
                </form>
              </div>
            </div>

            {/* Modal Content - Scrollable Receipt */}
            <div className="p-5 overflow-y-auto flex-1 font-mono text-sm" id="modal_view_bill_printable">
              {isBillLoading && !billData ? (
                <div className="py-12 flex flex-col items-center justify-center text-gray-400 gap-3">
                  <span className="loading loading-spinner loading-md text-amber-500"></span>
                  <p className="text-xs uppercase tracking-wider">Loading active order bill...</p>
                </div>
              ) : !billData?.hasActiveOrder ? (
                <div className="py-12 flex flex-col items-center justify-center text-center px-4">
                  <div className="w-16 h-16 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center mb-3">
                    <IconReceipt size={32} />
                  </div>
                  <h4 className="font-bold text-base text-gray-800 dark:text-gray-100">No Active Orders Found</h4>
                  <p className="text-xs text-gray-500 mt-1 max-w-xs">
                    There are no unsettled or active orders on {billData?.tableTitle || (room ? `Room ${room}` : "this table")} right now.
                  </p>
                  <button
                    type="button"
                    onClick={() => fetchActiveBill(true)}
                    className="mt-4 px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded-xl text-xs font-bold transition flex items-center gap-1.5"
                  >
                    <IconRefresh size={14} className={isBillRefreshing ? "animate-spin" : ""} /> Refresh Status
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Receipt Header */}
                  <div className="text-center pb-3 border-b border-dashed border-gray-300 dark:border-gray-700">
                    <h3 className="font-black text-lg md:text-xl uppercase tracking-wider text-gray-900 dark:text-white">
                      {billData.store?.name || storeName || "Restaurant"}
                    </h3>
                    {billData.store?.address && (
                      <p className="text-xs text-gray-500 mt-0.5">{billData.store.address}</p>
                    )}
                    {billData.store?.phone && (
                      <p className="text-xs text-gray-500">Tel: {billData.store.phone}</p>
                    )}
                    {billData.store?.panNo && (
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mt-0.5">
                        PAN / VAT: {billData.store.panNo}
                      </p>
                    )}

                    <div className="mt-2 inline-block px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 font-bold text-xs uppercase tracking-wider border border-amber-500/20">
                      ESTIMATE BILL (कच्चा बिल / UNSETTLED)
                    </div>
                  </div>

                  {/* Order & Location Meta Info */}
                  <div className="text-xs text-gray-600 dark:text-gray-300 grid grid-cols-2 gap-2 pb-3 border-b border-dashed border-gray-300 dark:border-gray-700">
                    <div>
                      <span className="text-gray-400 block text-[10px] uppercase">Location:</span>
                      <strong className="text-sm font-bold text-gray-900 dark:text-white">
                        {billData.tableTitle || (room ? `Room ${room}` : "Table")}
                      </strong>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-400 block text-[10px] uppercase">Token(s):</span>
                      <strong className="text-sm font-bold text-amber-600 dark:text-amber-400">
                        {billData.tokenNumbersText || "N/A"}
                      </strong>
                    </div>
                    <div>
                      <span className="text-gray-400 block text-[10px] uppercase">Order ID(s):</span>
                      <span>#{billData.orderIdsText || "N/A"}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-400 block text-[10px] uppercase">Date & Time:</span>
                      <span>{new Date(billData.firstOrderDate || Date.now()).toLocaleDateString()} {new Date(billData.firstOrderDate || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    {billData.customerName && (
                      <div className="col-span-2">
                        <span className="text-gray-400 text-[10px] uppercase mr-1">Guest:</span>
                        <span className="font-semibold">{billData.customerName}</span>
                      </div>
                    )}
                  </div>

                  {/* Itemized Table */}
                  <div>
                    <div className="grid grid-cols-12 text-[11px] font-bold uppercase text-gray-400 pb-1 border-b border-gray-200 dark:border-gray-700">
                      <div className="col-span-6">Item</div>
                      <div className="col-span-2 text-center">Qty</div>
                      <div className="col-span-2 text-right">Rate</div>
                      <div className="col-span-2 text-right">Total</div>
                    </div>

                    <div className="divide-y divide-gray-100 dark:divide-restro-gray/40">
                      {billData.items?.map((item, idx) => (
                        <div key={idx} className={`py-2 grid grid-cols-12 items-start text-xs ${item.is_cancelled ? "bg-red-500/5 -mx-2 px-2 rounded-lg" : ""}`}>
                          <div className="col-span-6 pr-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`font-medium ${item.is_cancelled ? "line-through text-red-500/80" : "text-gray-800 dark:text-gray-100"}`}>
                                {item.title}
                              </span>
                              {item.variant_title && (
                                <span className={`text-[11px] ${item.is_cancelled ? "line-through text-red-400" : "text-gray-400"}`}>
                                  ({item.variant_title})
                                </span>
                              )}
                              {item.is_cancelled && (
                                <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 border border-red-300 dark:border-red-800">
                                  Cancelled
                                </span>
                              )}
                              {item.is_complementary && (
                                <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400 border border-blue-300">
                                  Comp
                                </span>
                              )}
                            </div>

                            {/* Addons */}
                            {Array.isArray(item.addons) && item.addons.length > 0 && (
                              <div className="text-[11px] text-gray-500 dark:text-gray-400 pl-2 mt-0.5 space-y-0.5">
                                {item.addons.map((a, aidx) => (
                                  <div key={aidx}>+ {a.title} {a.price ? `(${currency} ${a.price})` : ""}</div>
                                ))}
                              </div>
                            )}

                            {/* Notes */}
                            {item.notes && (
                              <div className="text-[11px] text-amber-600 dark:text-amber-400 italic pl-2 mt-0.5">
                                Note: {item.notes}
                              </div>
                            )}

                            {/* Cancel Reason */}
                            {item.is_cancelled && item.cancel_reason && (
                              <div className="text-[11px] text-red-500 italic pl-2 mt-0.5">
                                Reason: {item.cancel_reason}
                              </div>
                            )}
                          </div>

                          <div className={`col-span-2 text-center font-semibold ${item.is_cancelled ? "line-through text-red-400" : "text-gray-700 dark:text-gray-200"}`}>
                            {item.quantity}
                          </div>

                          <div className={`col-span-2 text-right ${item.is_cancelled ? "line-through text-gray-400 text-[11px]" : "text-gray-600 dark:text-gray-300"}`}>
                            {Number(item.unit_price).toFixed(2)}
                          </div>

                          <div className={`col-span-2 text-right font-bold ${item.is_cancelled ? "text-red-500" : "text-gray-900 dark:text-white"}`}>
                            {item.is_cancelled ? (
                              <div>
                                <span className="line-through text-[10px] text-gray-400 block">
                                  {Number(item.original_line_total || 0).toFixed(2)}
                                </span>
                                <span>0.00</span>
                              </div>
                            ) : (
                              Number(item.line_total).toFixed(2)
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Receipt Summary Breakdown */}
                  <div className="pt-3 border-t-2 border-dashed border-gray-300 dark:border-gray-700 space-y-1.5 text-xs">
                    <div className="flex justify-between text-gray-600 dark:text-gray-300">
                      <span>Subtotal (Items Total):</span>
                      <span className="font-semibold">{currency} {Number(billData.subtotal || 0).toFixed(2)}</span>
                    </div>

                    {Number(billData.serviceChargeRate || 0) > 0 && Number(billData.serviceChargeTotal || 0) > 0 && (
                      <div className="flex justify-between text-gray-600 dark:text-gray-300">
                        <span>Service Charge ({billData.serviceChargeRate}%):</span>
                        <span className="font-semibold">{currency} {Number(billData.serviceChargeTotal || 0).toFixed(2)}</span>
                      </div>
                    )}

                    {Number(billData.taxTotal || 0) > 0 && (
                      <div className="flex justify-between text-gray-600 dark:text-gray-300">
                        <span>Taxes / VAT:</span>
                        <span className="font-semibold">{currency} {Number(billData.taxTotal || 0).toFixed(2)}</span>
                      </div>
                    )}

                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-between items-baseline font-black text-base md:text-lg text-emerald-700 dark:text-emerald-400">
                      <span>Grand Total:</span>
                      <span>{currency} {Number(billData.total || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Footer note */}
                  <div className="text-center pt-2 text-[11px] text-gray-400 italic border-t border-dashed border-gray-200 dark:border-gray-700">
                    * This is a running estimate of active orders. Final tax invoice will be issued upon settlement at reception/checkout.
                  </div>
                </div>
              )}
            </div>

            {/* Modal Bottom Actions */}
            <div className="px-5 py-3 bg-gray-50 dark:bg-restro-gray/40 border-t border-gray-100 dark:border-restro-gray flex items-center justify-between gap-2 no-print">
              <button
                type="button"
                onClick={() => fetchActiveBill(true)}
                disabled={isBillRefreshing || isBillLoading}
                className="btn btn-sm rounded-xl bg-white dark:bg-restro-gray border border-gray-200 dark:border-restro-gray text-gray-700 dark:text-gray-200 hover:bg-gray-100 flex items-center gap-1.5"
              >
                <IconRefresh size={16} className={isBillRefreshing ? "animate-spin text-amber-600" : ""} />
                <span>Refresh</span>
              </button>

              <div className="flex items-center gap-2">
                {billData?.hasActiveOrder && (
                  <button
                    type="button"
                    onClick={handlePrintEstimateBill}
                    className="btn btn-sm rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold border-none shadow flex items-center gap-1.5"
                  >
                    <SafeIconPrinter size={16} />
                    <span>Print Bill</span>
                  </button>
                )}
                <form method="dialog">
                  <button className="btn btn-sm btn-ghost rounded-xl">
                    Close
                  </button>
                </form>
              </div>
            </div>
          </div>
        </dialog>

        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            body * {
              visibility: hidden;
            }
            #modal_view_bill_printable, #modal_view_bill_printable * {
              visibility: visible;
            }
            #modal_view_bill_printable {
              position: fixed;
              left: 0;
              top: 0;
              width: 100%;
              max-width: 80mm;
              margin: 0 auto;
              padding: 10px;
              background: white !important;
              color: black !important;
            }
            .no-print {
              display: none !important;
            }
          }
        `}} />
      </div>
    </div>
  );
}
